import React, { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Link } from 'react-router-dom';
import { Folder, Home, FileText, Shield, Wrench, LayoutGrid, List } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { fetchHospedajes } from '@/services/hospedajes.service';
import { fetchEdificios } from '@/pages/parametrizacion/edificios/Services/edificios.services';
import { Input } from '@/components/ui/input';
import StorageUsageIndicator from '@/components/documents/StorageUsageIndicator';

export default function HospedajesDocsHome() {
  const [hospedajes, setHospedajes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [view, setView] = useState('grid'); // 'grid' | 'list'
  const [edificios, setEdificios] = useState([]);
  const [edificioId, setEdificioId] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    // Cargar preferencia guardada
    try {
      const saved = localStorage.getItem('hospedajes-docs:view');
      if (saved === 'grid' || saved === 'list') setView(saved);
    } catch {}

    const load = async () => {
      setLoading(true);
      setError(null);
      try {
        const query = { estado: 'Activo' };
        if (edificioId) query['edificio.id'] = edificioId;
        const { data } = await fetchHospedajes({ limit: 200, offset: 1, query });
        setHospedajes(Array.isArray(data) ? data : []);
      } catch (e) {
        setError(e?.message || 'No se pudo cargar la lista de hospedajes');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [edificioId]);

  useEffect(() => {
    const loadEdificios = async () => {
      try {
        const { data } = await fetchEdificios({ limit: 100, offset: 1, query: {} });
        setEdificios(Array.isArray(data) ? data : []);
      } catch (e) {
        console.error(e);
      }
    };
    loadEdificios();
  }, []);

  useEffect(() => {
    // Guardar preferencia
    try {
      localStorage.setItem('hospedajes-docs:view', view);
    } catch {}
  }, [view]);

  const displayedHospedajes = useMemo(() => {
    const term = searchTerm.trim();
    if (!term) return hospedajes;
    return hospedajes.filter(hosp => {
      const nombre = hosp?.nombrePropiedad || '';
      const edificio = hosp?.edificio?.nombre || '';
      const coordinador = hosp?.coordinador?.nombre || '';
      return (
        nombre.toLowerCase().includes(term.toLowerCase()) ||
        edificio.toLowerCase().includes(term.toLowerCase()) ||
        coordinador.toLowerCase().includes(term.toLowerCase())
      );
    });
  }, [hospedajes, searchTerm]);

  return (
    <div className="space-y-6">
      {/* Breadcrumb */}
      <div className="text-sm text-muted-foreground">
        <Link to="/documentacion" className="hover:underline">Documentación</Link>
        <span className="mx-1">/</span>
        <span className="text-foreground">Hospedajes</span>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2">
        <div>
          <h1 className="text-2xl font-semibold">Hospedajes</h1>
          <p className="text-sm text-muted-foreground">Gestiona la documentación de hospedajes.</p>
          <StorageUsageIndicator />
        </div>
        <div className="flex items-center gap-1 self-start sm:self-auto">
          <Button
            variant={view === 'grid' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('grid')}
            className="h-8"
            title="Vista de cuadrícula"
          >
            <LayoutGrid className="h-4 w-4" />
          </Button>
          <Button
            variant={view === 'list' ? 'default' : 'outline'}
            size="sm"
            onClick={() => setView('list')}
            className="h-8"
            title="Vista de lista"
          >
            <List className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Filtros */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        <div>
          <label className="text-xs text-muted-foreground">Edificio</label>
          <select
            className="mt-1 w-full h-9 rounded-md border bg-background px-3 text-sm"
            value={edificioId}
            onChange={e => setEdificioId(e.target.value)}
          >
            <option value="">Todos</option>
            {edificios.map(e => (
              <option key={e._id || e.id} value={e._id || e.id}>{e.nombre || e.name}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-xs text-muted-foreground">Buscar</label>
          <Input
            className="mt-1 h-9"
            placeholder="Buscar por nombre, edificio o coordinador"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {/* Content */}
      {loading && <div className="text-sm text-muted-foreground">Cargando hospedajes...</div>}
      {error && <div className="text-sm text-destructive">{error}</div>}

      {view === 'grid' ? (
        <div className="grid gap-3 sm:gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
          {displayedHospedajes.map(hosp => (
            <Link key={hosp._id || hosp.id} to={`/documentacion/hospedajes/${hosp._id || hosp.id}`} className="group">
              <Card className="transition-colors hover:bg-muted/50">
                <CardContent className="p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
                  <div className="relative">
                    <Folder className="h-9 w-9 sm:h-12 sm:w-12 text-green-500" />
                    <Home className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-foreground absolute -right-1 -bottom-1" />
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium group-hover:text-primary truncate">
                      {hosp.nombrePropiedad || 'Sin nombre'}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {hosp.edificio?.nombre || ''} • {hosp.coordinador?.nombre || ''}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {hosp.tipo === 'vertical' ? 'Vertical' : 'Horizontal'} • {hosp.numeroCamas || 0} camas
                    </div>
                  </div>
                </CardContent>
              </Card>
            </Link>
          ))}

          {/* Contratos Generales */}
          <div className="group">
            <Card className="transition-colors hover:bg-muted/50 cursor-default">
              <CardContent className="p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
                <div className="relative">
                  <Folder className="h-9 w-9 sm:h-12 sm:w-12 text-blue-500" />
                  <FileText className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-foreground absolute -right-1 -bottom-1" />
                </div>
                <div>
                  <div className="font-medium group-hover:text-primary">Contratos Generales</div>
                  <div className="text-xs text-muted-foreground">Contratos marco y documentos legales.</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Políticas y Procedimientos */}
          <div className="group">
            <Card className="transition-colors hover:bg-muted/50 cursor-default">
              <CardContent className="p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
                <div className="relative">
                  <Folder className="h-9 w-9 sm:h-12 sm:w-12 text-purple-500" />
                  <Shield className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-foreground absolute -right-1 -bottom-1" />
                </div>
                <div>
                  <div className="font-medium group-hover:text-primary">Políticas</div>
                  <div className="text-xs text-muted-foreground">Políticas y procedimientos operativos.</div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Reportes y Auditorías */}
          <div className="group">
            <Card className="transition-colors hover:bg-muted/50 cursor-default">
              <CardContent className="p-4 sm:p-5 flex items-center gap-3 sm:gap-4">
                <div className="relative">
                  <Folder className="h-9 w-9 sm:h-12 sm:w-12 text-orange-500" />
                  <Wrench className="h-3.5 w-3.5 sm:h-4 sm:w-4 text-foreground absolute -right-1 -bottom-1" />
                </div>
                <div>
                  <div className="font-medium group-hover:text-primary">Reportes</div>
                  <div className="text-xs text-muted-foreground">Reportes de auditoría y mantenimiento.</div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      ) : (
        <div className="divide-y rounded-md border">
          {displayedHospedajes.map(hosp => (
            <Link key={hosp._id || hosp.id} to={`/documentacion/hospedajes/${hosp._id || hosp.id}`} className="block hover:bg-muted/40">
              <div className="px-3 sm:px-4 py-3 flex items-center gap-3">
                <div className="relative">
                  <Folder className="h-6 w-6 text-green-500" />
                  <Home className="h-3 w-3 text-foreground absolute -right-1 -bottom-1" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {hosp.nombrePropiedad || 'Sin nombre'}
                  </div>
                  <div className="text-xs text-muted-foreground truncate">
                    {hosp.edificio?.nombre || ''} • {hosp.coordinador?.nombre || ''} • {hosp.tipo === 'vertical' ? 'Vertical' : 'Horizontal'} • {hosp.numeroCamas || 0} camas
                  </div>
                </div>
              </div>
            </Link>
          ))}

          {/* Secciones adicionales en vista lista */}
          <div className="px-3 sm:px-4 py-3 flex items-center gap-3 cursor-default">
            <div className="relative">
              <Folder className="h-6 w-6 text-blue-500" />
              <FileText className="h-3 w-3 text-foreground absolute -right-1 -bottom-1" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">Contratos Generales</div>
              <div className="text-xs text-muted-foreground truncate">Contratos marco y documentos legales</div>
            </div>
          </div>

          <div className="px-3 sm:px-4 py-3 flex items-center gap-3 cursor-default">
            <div className="relative">
              <Folder className="h-6 w-6 text-purple-500" />
              <Shield className="h-3 w-3 text-foreground absolute -right-1 -bottom-1" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">Políticas</div>
              <div className="text-xs text-muted-foreground truncate">Políticas y procedimientos operativos</div>
            </div>
          </div>

          <div className="px-3 sm:px-4 py-3 flex items-center gap-3 cursor-default">
            <div className="relative">
              <Folder className="h-6 w-6 text-orange-500" />
              <Wrench className="h-3 w-3 text-foreground absolute -right-1 -bottom-1" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium truncate">Reportes</div>
              <div className="text-xs text-muted-foreground truncate">Reportes de auditoría y mantenimiento</div>
            </div>
          </div>
        </div>
      )}

      {!loading && !error && displayedHospedajes.length === 0 && (
        <div className="text-sm text-muted-foreground text-center py-8">
          No se encontraron hospedajes con los filtros aplicados.
        </div>
      )}
    </div>
  );
}
